"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConfluenceRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number | null;
  volume_ratio: number | null;
  uin_pct: number | null;
  bullish_confluence_score: number;
  technical_points: number;
  market_points: number;
  technical_signals: string[];
  market_signals: string[];
  risk_flags: string[];
  blockers: string[];
};

type DefensibleRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number | null;
  volume_ratio: number | null;
  uin_pct: number | null;
  defensible_score: number;
  anchor_signals: string[];
  technical_signals: string[];
  market_signals: string[];
  market_confirmations: string[];
  risk_flags: string[];
  blockers: string[];
};

type MarketContext = {
  pressure_regime: string;
  pressure_value: number;
  flow_verdict: string;
  top_bullish_sector: string;
  financing_stress?: boolean;
};

type SmartScanData = {
  ok: boolean;
  generated_at: string;
  as_of: string;
  market_context: MarketContext;
  boards: {
    bullish_confluence: { label: string; results_count: number; results: ConfluenceRow[] };
    defensible_bullish: { label: string; results_count: number; results: DefensibleRow[] };
  };
  diagnostics: {
    technical_sources_loaded: number;
    market_sources_loaded: number;
    skipped_sources: string[];
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (v: number | null | undefined, d = 2) =>
  v != null && Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: d }) : "—";

const SIGNAL_LABELS: Record<string, string> = {
  tomorrow_potential: "Tomorrow",
  premium_next_day: "Premium",
  hiran_confluence: "Hiran",
  renko_ichimoku: "Renko-Ichi",
  price_action_strict: "Price Action",
  volume_abnormal_bullish: "Vol Surge",
  bullish_divergence: "Divergence",
  elliott_wave3: "Elliott W3",
  renko_first_green: "1st Green",
  renko_pnf_bullish: "PnF Break",
  uin_strong: "UIN Strong",
  uin_high_conviction: "UIN HC",
  sector_flow_positive: "Flow+",
  premium_normal_risk: "Clean Risk",
  funded_long_supportive: "Funded Long",
  off_market_supportive: "Off-Mkt",
  low_pressure_regime: "Low Pressure",
};

const RISK_LABELS: Record<string, string> = {
  speculative_uin: "Speculative UIN",
  premium_elevated_risk: "Elevated Risk",
  high_leverage_warning: "High Leverage",
  unwinding_risk: "Unwind Risk",
  blank_sale_heavy: "Blank Sale",
  high_pressure_regime: "High Pressure",
  financing_stress: "Fin. Stress",
};

const ANCHOR_SET = new Set([
  "tomorrow_potential", "premium_next_day", "hiran_confluence",
  "renko_ichimoku", "price_action_strict",
]);

const scoreColor = (score: number) => {
  if (score >= 70) return "var(--color-success, #10b981)";
  if (score >= 50) return "#f59e0b";
  return "rgba(148,163,184,0.7)";
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ScoreBar({ score, color }: { score: number; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 72, height: 5, background: "rgba(148,163,184,0.15)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${score}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontWeight: 700, color, fontSize: "0.85rem" }}>{score.toFixed(1)}</span>
    </div>
  );
}

function SignalPill({ label, type }: { label: string; type: "anchor" | "support" | "market" | "risk" }) {
  const colors = {
    anchor: { bg: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.4)", color: "#10b981" },
    support: { bg: "rgba(59,130,246,0.18)", border: "rgba(59,130,246,0.4)", color: "#60a5fa" },
    market: { bg: "rgba(139,92,246,0.18)", border: "rgba(139,92,246,0.4)", color: "#a78bfa" },
    risk: { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.35)", color: "#f87171" },
  }[type];
  return (
    <span style={{
      fontSize: "0.7rem", fontWeight: 600, padding: "2px 7px", borderRadius: 6,
      background: colors.bg, border: `1px solid ${colors.border}`, color: colors.color,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function ExpandedRow({ signals, marketSignals, riskFlags }: {
  signals: string[];
  marketSignals: string[];
  riskFlags: string[];
}) {
  return (
    <div style={{ padding: "10px 16px", background: "rgba(15,23,42,0.6)", borderTop: "1px solid rgba(148,163,184,0.08)" }}>
      {signals.length > 0 && (
        <div style={{ marginBottom: 6, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
          <span style={{ fontSize: "0.68rem", color: "rgba(148,163,184,0.55)", fontWeight: 700, marginRight: 4, whiteSpace: "nowrap" }}>TECHNICAL</span>
          {signals.map(s => <SignalPill key={s} label={SIGNAL_LABELS[s] ?? s} type={ANCHOR_SET.has(s) ? "anchor" : "support"} />)}
        </div>
      )}
      {marketSignals.length > 0 && (
        <div style={{ marginBottom: 6, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
          <span style={{ fontSize: "0.68rem", color: "rgba(148,163,184,0.55)", fontWeight: 700, marginRight: 4, whiteSpace: "nowrap" }}>MARKET</span>
          {marketSignals.map(s => <SignalPill key={s} label={SIGNAL_LABELS[s] ?? s} type="market" />)}
        </div>
      )}
      {riskFlags.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
          <span style={{ fontSize: "0.68rem", color: "rgba(148,163,184,0.55)", fontWeight: 700, marginRight: 4, whiteSpace: "nowrap" }}>FLAGS</span>
          {riskFlags.map(s => <SignalPill key={s} label={RISK_LABELS[s] ?? s} type="risk" />)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table components
// ---------------------------------------------------------------------------

const hStyle = { padding: "9px 12px", textAlign: "left" as const, fontSize: "0.68rem", fontWeight: 700, color: "rgba(148,163,184,0.55)", borderBottom: "1px solid rgba(148,163,184,0.1)", whiteSpace: "nowrap" as const };
const cStyle = { padding: "11px 12px", fontSize: "0.82rem", borderBottom: "1px solid rgba(148,163,184,0.06)", verticalAlign: "middle" as const };

function ConfluenceTable({ rows, expandedSymbols, onToggle }: {
  rows: ConfluenceRow[];
  expandedSymbols: Set<string>;
  onToggle: (sym: string) => void;
}) {
  if (rows.length === 0) return <p style={{ color: "rgba(148,163,184,0.45)", padding: "20px 0" }}>No results match current filters.</p>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={hStyle}>#</th>
            <th style={hStyle}>Symbol</th>
            <th style={hStyle}>Score</th>
            <th style={hStyle}>Tech Pts</th>
            <th style={hStyle}>Signals</th>
            <th style={hStyle}>Vol Ratio</th>
            <th style={hStyle}>UIN %</th>
            <th style={hStyle}>Sector</th>
            <th style={hStyle}>Risk</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const expanded = expandedSymbols.has(row.symbol);
            const color = scoreColor(row.bullish_confluence_score);
            return (
              <>
                <tr key={row.symbol} onClick={() => onToggle(row.symbol)} style={{ cursor: "pointer" }}>
                  <td style={{ ...cStyle, color: "rgba(148,163,184,0.4)", fontWeight: 600 }}>{i + 1}</td>
                  <td style={cStyle}>
                    <div style={{ fontWeight: 700 }}>
                      <Link href={`/stock/${row.symbol}`} style={{ color: "var(--color-text)" }} onClick={e => e.stopPropagation()}>{row.symbol}</Link>
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.55)" }}>{row.name}</div>
                  </td>
                  <td style={cStyle}><ScoreBar score={row.bullish_confluence_score} color={color} /></td>
                  <td style={{ ...cStyle, fontWeight: 700, color }}>{row.technical_points}</td>
                  <td style={cStyle}>
                    <span style={{ fontWeight: 700 }}>{row.technical_signals.length}</span>
                    <span style={{ color: "rgba(148,163,184,0.35)", fontSize: "0.75rem" }}> tech · </span>
                    <span style={{ fontWeight: 700, color: "#a78bfa" }}>{row.market_signals.length}</span>
                    <span style={{ color: "rgba(148,163,184,0.35)", fontSize: "0.75rem" }}> mkt</span>
                  </td>
                  <td style={{ ...cStyle, fontWeight: 600 }}>{fmt(row.volume_ratio)}x</td>
                  <td style={{ ...cStyle, fontWeight: 600 }}>{row.uin_pct != null ? `${fmt(row.uin_pct, 1)}%` : "—"}</td>
                  <td style={{ ...cStyle, fontSize: "0.73rem", color: "rgba(148,163,184,0.65)" }}>{row.sector}</td>
                  <td style={cStyle}>
                    {row.blockers.length > 0
                      ? <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f87171" }}>⚠ {row.blockers.length} blocker{row.blockers.length > 1 ? "s" : ""}</span>
                      : row.risk_flags.length > 0
                        ? <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#f59e0b" }}>~ {row.risk_flags.length} flag{row.risk_flags.length > 1 ? "s" : ""}</span>
                        : <span style={{ fontSize: "0.72rem", color: "#10b981" }}>Clean</span>}
                  </td>
                </tr>
                {expanded && (
                  <tr key={`${row.symbol}-exp`}>
                    <td colSpan={9} style={{ padding: 0 }}>
                      <ExpandedRow signals={row.technical_signals} marketSignals={row.market_signals} riskFlags={row.risk_flags} />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DefensibleTable({ rows, expandedSymbols, onToggle }: {
  rows: DefensibleRow[];
  expandedSymbols: Set<string>;
  onToggle: (sym: string) => void;
}) {
  if (rows.length === 0) return <p style={{ color: "rgba(148,163,184,0.45)", padding: "20px 0" }}>No defensible setups match current filters.</p>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={hStyle}>#</th>
            <th style={hStyle}>Symbol</th>
            <th style={hStyle}>Score</th>
            <th style={hStyle}>Anchors</th>
            <th style={hStyle}>Market Conf.</th>
            <th style={hStyle}>Vol Ratio</th>
            <th style={hStyle}>UIN %</th>
            <th style={hStyle}>Sector</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const expanded = expandedSymbols.has(row.symbol);
            const color = scoreColor(row.defensible_score);
            return (
              <>
                <tr key={row.symbol} onClick={() => onToggle(row.symbol)} style={{ cursor: "pointer" }}>
                  <td style={{ ...cStyle, color: "rgba(148,163,184,0.4)", fontWeight: 600 }}>{i + 1}</td>
                  <td style={cStyle}>
                    <div style={{ fontWeight: 700 }}>
                      <Link href={`/stock/${row.symbol}`} style={{ color: "var(--color-text)" }} onClick={e => e.stopPropagation()}>{row.symbol}</Link>
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.55)" }}>{row.name}</div>
                  </td>
                  <td style={cStyle}><ScoreBar score={row.defensible_score} color={color} /></td>
                  <td style={cStyle}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                      {row.anchor_signals.map(s => <SignalPill key={s} label={SIGNAL_LABELS[s] ?? s} type="anchor" />)}
                    </div>
                  </td>
                  <td style={cStyle}>
                    <span style={{ fontWeight: 700, color: "#a78bfa" }}>{row.market_confirmations.length}</span>
                    <span style={{ color: "rgba(148,163,184,0.35)", fontSize: "0.75rem" }}> confirmed</span>
                  </td>
                  <td style={{ ...cStyle, fontWeight: 600 }}>{fmt(row.volume_ratio)}x</td>
                  <td style={{ ...cStyle, fontWeight: 600 }}>{row.uin_pct != null ? `${fmt(row.uin_pct, 1)}%` : "—"}</td>
                  <td style={{ ...cStyle, fontSize: "0.73rem", color: "rgba(148,163,184,0.65)" }}>{row.sector}</td>
                </tr>
                {expanded && (
                  <tr key={`${row.symbol}-exp`}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <ExpandedRow signals={row.technical_signals} marketSignals={row.market_signals} riskFlags={row.risk_flags} />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Board = "confluence" | "defensible";
const SECTOR_ALL = "All Sectors";

export default function SmartScanPage() {
  const [data, setData] = useState<SmartScanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [board, setBoard] = useState<Board>("confluence");
  const [minScore, setMinScore] = useState(40);
  const [minSignals, setMinSignals] = useState(0);
  const [onlyUin, setOnlyUin] = useState(false);
  const [onlyNoBlockers, setOnlyNoBlockers] = useState(false);
  const [sectorFilter, setSectorFilter] = useState(SECTOR_ALL);
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetch("/api/scanner/smart-scan")
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  const sectors = useMemo(() => {
    if (!data) return [SECTOR_ALL];
    const all = data.boards.bullish_confluence.results.map(r => r.sector).filter(Boolean);
    return [SECTOR_ALL, ...Array.from(new Set(all)).sort()];
  }, [data]);

  const filteredConfluence = useMemo(() => {
    if (!data) return [];
    return data.boards.bullish_confluence.results.filter(r => {
      if (r.bullish_confluence_score < minScore) return false;
      if (r.technical_signals.length < minSignals) return false;
      if (onlyUin && (r.uin_pct ?? 0) < 50) return false;
      if (onlyNoBlockers && r.blockers.length > 0) return false;
      if (sectorFilter !== SECTOR_ALL && r.sector !== sectorFilter) return false;
      return true;
    });
  }, [data, minScore, minSignals, onlyUin, onlyNoBlockers, sectorFilter]);

  const filteredDefensible = useMemo(() => {
    if (!data) return [];
    return data.boards.defensible_bullish.results.filter(r => {
      if (r.defensible_score < minScore) return false;
      if (sectorFilter !== SECTOR_ALL && r.sector !== sectorFilter) return false;
      return true;
    });
  }, [data, minScore, sectorFilter]);

  const toggleExpand = (sym: string) => {
    setExpandedSymbols(prev => {
      const next = new Set(prev);
      next.has(sym) ? next.delete(sym) : next.add(sym);
      return next;
    });
  };

  const ctx = data?.market_context;

  if (loading) return <div style={{ padding: 40, color: "rgba(148,163,184,0.7)" }}>Loading Smart Scan…</div>;
  if (error || !data?.ok) return <div style={{ padding: 40, color: "#f87171" }}>Error: {error ?? "No data available. Run the smart_scan job first."}</div>;

  return (
    <div style={{ padding: "24px 20px", maxWidth: 1120, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: "1.45rem", fontWeight: 800, margin: 0 }}>Smart Scan</h1>
        <p style={{ color: "rgba(148,163,184,0.6)", fontSize: "0.82rem", margin: "4px 0 0" }}>
          As of {data.as_of} · Updated {new Date(data.generated_at).toLocaleTimeString()}
          {" · "}{data.diagnostics.technical_sources_loaded}/11 sources loaded
        </p>
      </div>

      {/* Market context chips */}
      {ctx && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {ctx.pressure_regime && (
            <span style={{
              fontSize: "0.73rem", fontWeight: 600, padding: "4px 10px", borderRadius: 8,
              background: ctx.pressure_regime.includes("Low") ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)",
              border: `1px solid ${ctx.pressure_regime.includes("Low") ? "rgba(16,185,129,0.35)" : "rgba(239,68,68,0.35)"}`,
              color: ctx.pressure_regime.includes("Low") ? "#10b981" : "#f87171",
            }}>{ctx.pressure_regime}</span>
          )}
          {ctx.flow_verdict && (
            <span style={{ fontSize: "0.73rem", fontWeight: 600, padding: "4px 10px", borderRadius: 8, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.28)", color: "#93c5fd" }}>
              {ctx.flow_verdict}
            </span>
          )}
          {ctx.top_bullish_sector && (
            <span style={{ fontSize: "0.73rem", fontWeight: 600, padding: "4px 10px", borderRadius: 8, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.28)", color: "#fbbf24" }}>
              Top: {ctx.top_bullish_sector}
            </span>
          )}
          {ctx.financing_stress && (
            <span style={{ fontSize: "0.73rem", fontWeight: 600, padding: "4px 10px", borderRadius: 8, background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.35)", color: "#f87171" }}>
              Financing Stress
            </span>
          )}
        </div>
      )}

      {/* Board toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {(["confluence", "defensible"] as Board[]).map(b => (
          <button key={b} onClick={() => setBoard(b)} style={{
            padding: "7px 16px", borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
            border: "1px solid",
            background: board === b ? "rgba(16,185,129,0.18)" : "rgba(15,23,42,0.4)",
            borderColor: board === b ? "rgba(16,185,129,0.5)" : "rgba(148,163,184,0.18)",
            color: board === b ? "#10b981" : "rgba(148,163,184,0.75)",
          }}>
            {b === "confluence"
              ? `Bullish Confluence (${filteredConfluence.length})`
              : `Defensible (${filteredDefensible.length})`}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: "0.68rem", color: "rgba(148,163,184,0.5)", fontWeight: 700, marginRight: 4 }}>MIN SCORE</span>
          {[30, 40, 50, 60].map(v => (
            <button key={v} onClick={() => setMinScore(v)} style={{
              padding: "4px 9px", borderRadius: 7, fontWeight: 700, fontSize: "0.76rem", cursor: "pointer",
              border: "1px solid",
              background: minScore === v ? "rgba(59,130,246,0.18)" : "rgba(15,23,42,0.4)",
              borderColor: minScore === v ? "rgba(59,130,246,0.5)" : "rgba(148,163,184,0.13)",
              color: minScore === v ? "#60a5fa" : "rgba(148,163,184,0.65)",
            }}>{v}+</button>
          ))}
        </div>
        {board === "confluence" && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: "0.68rem", color: "rgba(148,163,184,0.5)", fontWeight: 700, marginRight: 4 }}>SIGNALS</span>
            {[0, 2, 3, 5].map(v => (
              <button key={v} onClick={() => setMinSignals(v)} style={{
                padding: "4px 9px", borderRadius: 7, fontWeight: 700, fontSize: "0.76rem", cursor: "pointer",
                border: "1px solid",
                background: minSignals === v ? "rgba(59,130,246,0.18)" : "rgba(15,23,42,0.4)",
                borderColor: minSignals === v ? "rgba(59,130,246,0.5)" : "rgba(148,163,184,0.13)",
                color: minSignals === v ? "#60a5fa" : "rgba(148,163,184,0.65)",
              }}>{v === 0 ? "Any" : `${v}+`}</button>
            ))}
          </div>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: "0.78rem", color: "rgba(148,163,184,0.75)", fontWeight: 600 }}>
          <input type="checkbox" checked={onlyUin} onChange={e => setOnlyUin(e.target.checked)} />
          UIN ≥ 50%
        </label>
        {board === "confluence" && (
          <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: "0.78rem", color: "rgba(148,163,184,0.75)", fontWeight: 600 }}>
            <input type="checkbox" checked={onlyNoBlockers} onChange={e => setOnlyNoBlockers(e.target.checked)} />
            No Blockers
          </label>
        )}
        <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)} style={{
          padding: "5px 9px", borderRadius: 8, background: "rgba(15,23,42,0.5)",
          border: "1px solid rgba(148,163,184,0.18)", color: "var(--color-text)", fontSize: "0.78rem", fontWeight: 600,
        }}>
          {sectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Tables */}
      {board === "confluence" && (
        <ConfluenceTable rows={filteredConfluence} expandedSymbols={expandedSymbols} onToggle={toggleExpand} />
      )}
      {board === "defensible" && (
        <DefensibleTable rows={filteredDefensible} expandedSymbols={expandedSymbols} onToggle={toggleExpand} />
      )}

      {/* Diagnostics footer */}
      {data.diagnostics.skipped_sources.length > 0 && (
        <p style={{ fontSize: "0.7rem", color: "#f59e0b", marginTop: 14 }}>
          Skipped sources: {data.diagnostics.skipped_sources.join(", ")}
        </p>
      )}
    </div>
  );
}
